import type { StateRule } from '../catalogue/state-rules.js';
import type { ExplodeLine } from '../production/explode.js';
import type { PlainDate } from '../time/index.js';
import type { UomConversion } from '../uom/index.js';
import type { CoverMismatch } from './cover.js';

/**
 * Inputs and outputs of the feasibility simulator (plan §5.6). Plain data: the server loads
 * it from the database, a station from its synced tables; both run the same pure function.
 * Quantities are decimal strings in the product's base unit unless a `uom` says otherwise;
 * instants are ISO-8601 strings; days are the business's trading days (`YYYY-MM-DD`).
 * @module
 */

/**
 * The simulator's rules version, stored with every run and part of its inputs hash. Bump it
 * whenever what `simulate.ts` or `cover.ts` decide changes (not for a pure refactor): the
 * server then recomputes runs made under the old rules (`isFresh`), and a station whose
 * vendored engine differs from the office run's says its own check is out of date (CR7-101).
 * Two branches that both bump must not merge under one number: take the next free one.
 * History: 1 (plan §5.6), 2 (D-074 what covers a line), 3 (CR7-6 a started batch's day; and,
 * on another branch, D-074 freshness: both merged as 3), 4 (the two together; CR7-102).
 */
export const FEASIBILITY_ENGINE_VERSION = 4;

/** Planning horizon when none is given (D-102). */
export const DEFAULT_HORIZON_DAYS = 14;

/** A recipe version as the simulator needs it (pinned by a batch, or a product's active one). */
export interface FeasibilityRecipe {
  versionId: string;
  batchYieldQty: string;
  yieldUom: string;
  lines: readonly ExplodeLine[];
}

/** A product of the catalogue. */
export interface FeasibilityProduct {
  id: string;
  name: string;
  baseUom: string;
  roles: readonly string[];
  deductionMode: 'made_ahead' | 'made_to_order';
  shelfLifeDays: number | null;
  tracksExpiry: boolean;
  /** Its own state rule, else its category's; `null`: one state, usable and sellable. */
  stateRule: StateRule | null;
  /** Its signed-off recipe (made-to-order components are exploded through it). */
  activeRecipe: FeasibilityRecipe | null;
}

/** A location of the branch and what it feeds. */
export interface FeasibilityLocation {
  id: string;
  name: string;
  feedsProduction: boolean;
  feedsSale: boolean;
}

/** One stock balance row: a lot (or lot-less stock) at a location in a state. */
export interface FeasibilityStockRow {
  lotId: string | null;
  /** The code on the label (`L-K7Q-00412`), for the flags' words. */
  lotCode: string | null;
  productId: string;
  locationId: string;
  state: string;
  qty: string;
  bestBefore: PlainDate | null;
  producedAt: string | null;
}

/** An open reservation (`soft` / `claimed`): stock held for an order line or a hold. */
export interface FeasibilityReservation {
  id: string;
  productId: string;
  /** A claim on one lot, or `null` for a product-level hold. */
  lotId: string | null;
  locationId: string | null;
  qty: string;
  orderLineId: string | null;
}

/** A planned allocation of a batch's output (vault "destination-tagged production"). */
export interface FeasibilityBatchAllocation {
  id: string;
  destination: 'shelf' | 'order_claim' | 'branch_transfer' | 'immediate_order';
  qty: string;
  orderLineId: string | null;
}

/** A planned or started production batch. */
export interface FeasibilityBatch {
  id: string;
  number: string;
  productId: string;
  status: 'planned' | 'started';
  plannedStart: string;
  /** When a started batch started (its components are used from then). */
  startedAt: string | null;
  plannedQty: string;
  /** The version pinned at planning; `null` when it can't be read (`missing_data`). */
  recipe: FeasibilityRecipe | null;
  allocations: readonly FeasibilityBatchAllocation[];
  /**
   * The order line it is planned to make ("made for CO-… line 2", D-074). A custom-item
   * line is covered only by output of a batch planned for it.
   */
  forOrderLineId?: string | null;
}

/** What an open purchase order line still expects (uncertain until received). */
export interface FeasibilityReceipt {
  /** The PO line. */
  id: string;
  poId: string;
  poNumber: string;
  productId: string;
  /** Still to come, in the product's base unit. */
  qty: string;
  /** `null`: no expected date, so it can't be placed in time (not simulated). */
  expectedDate: PlainDate | null;
}

/** A line of a confirmed customer order. */
export interface FeasibilityOrderLine {
  id: string;
  orderId: string;
  orderNumber: string;
  position: number;
  /**
   * A catalogue product (covered only by output of batches of that product), or `null` for a
   * custom item (covered only by output of a batch planned for this line, D-074).
   */
  productId: string | null;
  description: string | null;
  qty: string;
  /** `null`: no promised time, so it can't be checked. */
  promisedAt: string | null;
}

/** Everything the simulator reads. */
export interface FeasibilityInput {
  /** "Now": nothing happens before it (overdue events are moved to it). */
  asOf: string;
  timeZone: string;
  /** The account's trading-day cut-off, `HH:mm`. */
  tradingDayCutoff: string;
  /** Default {@link DEFAULT_HORIZON_DAYS}. */
  horizonDays?: number;
  products: readonly FeasibilityProduct[];
  locations: readonly FeasibilityLocation[];
  conversions: readonly UomConversion[];
  stock: readonly FeasibilityStockRow[];
  reservations: readonly FeasibilityReservation[];
  batches: readonly FeasibilityBatch[];
  receipts: readonly FeasibilityReceipt[];
  orderLines: readonly FeasibilityOrderLine[];
}

// ---------------------------------------------------------------------------------------
// Output

/** The flags of §5.6 step 3. Flags recommend; they never block (plan §5.6 principle). */
export const FEASIBILITY_FLAG_CODES = [
  'expires_before_use',
  'shortfall',
  'depends_on_unconfirmed_receipt',
  'intermediate_not_planned',
  'missing_data',
] as const;
/** One of {@link FEASIBILITY_FLAG_CODES}. */
export type FeasibilityFlagCode = (typeof FEASIBILITY_FLAG_CODES)[number];

/** A product named in a flag. */
export interface FlagProductRef {
  id: string;
  name: string;
}

/** A lot named in a flag. */
export interface FlagLotRef {
  lotId: string | null;
  lotCode: string | null;
  locationId: string;
  bestBefore: PlainDate | null;
  qty: string;
}

/** An expected receipt named in a flag. */
export interface FlagReceiptRef {
  receiptId: string;
  poId: string;
  poNumber: string;
  expectedDate: PlainDate;
  qty: string;
}

/** Why data was missing. */
export type MissingDataReason =
  | 'no_recipe'
  | 'missing_conversion'
  | 'unknown_product'
  | 'unknown_component'
  | 'no_shelf_life'
  | 'made_to_order_without_recipe'
  | 'recipe_loop';

/** A feasibility flag, with the specifics the Admin and stations put into words. */
export type FeasibilityFlag =
  | {
      code: 'expires_before_use';
      message: string;
      data: {
        component: FlagProductRef;
        day: PlainDate;
        needed: string;
        missing: string;
        uom: string;
        /** The lots that exist but expire before the batch's day. */
        lots: FlagLotRef[];
      };
    }
  | {
      code: 'shortfall';
      message: string;
      data: {
        component: FlagProductRef;
        day: PlainDate;
        needed: string;
        available: string;
        missing: string;
        uom: string;
        /** Held for customer orders (never used by a batch: reservations are respected). */
        reservedForOrders: string;
        /** Earlier batches in the plan that used this component (largest first, at most 5). */
        earlierUse: { batchId: string; number: string; qty: string }[];
        /** Batches planned for this component (an intermediate planned too late or too small). */
        plannedBatches: { batchId: string; number: string; day: PlainDate }[];
      };
    }
  | {
      code: 'depends_on_unconfirmed_receipt';
      message: string;
      data: {
        component: FlagProductRef;
        day: PlainDate;
        qty: string;
        uom: string;
        receipts: FlagReceiptRef[];
      };
    }
  | {
      code: 'intermediate_not_planned';
      message: string;
      data: {
        component: FlagProductRef;
        day: PlainDate;
        needed: string;
        available: string;
        missing: string;
        uom: string;
      };
    }
  | {
      code: 'missing_data';
      message: string;
      data: {
        reason: MissingDataReason;
        product: FlagProductRef | { id: string; name: null };
        from?: string;
        to?: string;
      };
    };

/** A batch's verdict. `not_evaluated` is never counted as feasible (lesson 12). */
export type BatchVerdict = 'feasible' | 'at_risk' | 'infeasible' | 'not_evaluated';

/** One component of a batch: what the plan needs and what the simulation found. */
export interface FeasibilityComponent {
  productId: string;
  needed: string;
  allocated: string;
  uom: string;
  /** Part of `allocated` that exists only once a purchase order arrives. */
  fromReceipts: string;
  /** Part of `allocated` that exists only once an earlier planned batch is made. */
  fromBatches: string;
}

/**
 * Why a batch or an order line is at risk or short (plan §5.6: flags explain themselves,
 * D-074): the flag at the root, on the batch that carries it, or an expected delivery a line
 * counts on directly. Each names its lot, component, amount and day through its flag.
 */
export interface FeasibilityCause {
  /** The batch whose flag it is (`null`: a delivery a line counts on directly). */
  batchId: string | null;
  number: string | null;
  productId: string | null;
  verdict: BatchVerdict | null;
  /** What that batch is expected to make, of what is planned ("0 of 1 kg"). */
  expectedYield: string | null;
  plannedQty: string | null;
  /** The flag behind it (`null`: a delivery). */
  flag: FeasibilityFlag | null;
  /** The delivery a line counts on directly. */
  receipt: FlagReceiptRef | null;
  /** The cause in one sentence, naming the batch, product, lot, amount and day. */
  message: string;
}

/** The simulator's answer for one batch. */
export interface FeasibilityBatchResult {
  batchId: string;
  number: string;
  productId: string;
  status: 'planned' | 'started';
  plannedStart: string;
  /** The trading day it is judged on (its planned day, or today if overdue). */
  day: PlainDate;
  plannedQty: string;
  /** What the plan can make: planned × the scarcest component's share (whole units for `ea`). */
  expectedYield: string;
  verdict: BatchVerdict;
  evaluated: boolean;
  /** Why it could not be (fully) evaluated, in words. */
  notEvaluatedReasons: string[];
  flags: FeasibilityFlag[];
  components: FeasibilityComponent[];
  /** Planned batches whose output this batch uses. */
  dependsOn: { batchId: string; number: string }[];
  /**
   * What is wrong upstream: the flags of earlier batches it uses (or counts on) that can't be
   * made as planned or are at risk, traced to their root. Empty when nothing upstream is.
   */
  causes: FeasibilityCause[];
}

/** Line coverage (§5.6 step 5). */
export type LineCoverageStatus =
  'covered_by_stock' | 'covered_by_batch' | 'uncovered' | 'not_evaluated';

/** Why a line is uncovered. */
export type UncoveredReason =
  | 'short_yield'
  | 'batch_after_promise'
  | 'claim_expires'
  | 'custom_item_unplanned'
  | 'not_enough_stock';

/** Why a line could not be evaluated. */
export type LineNotEvaluatedReason =
  'no_promised_time' | 'unknown_product' | 'made_to_order_product' | 'depends_on_unevaluated_batch';

/** Where part of a line's quantity comes from. */
export interface LineSource {
  /** `claim`: stock already held for this line; `stock`: free stock on hand; `batch`: output of a planned batch; `receipt`: an expected delivery. */
  kind: 'claim' | 'stock' | 'batch' | 'receipt';
  qty: string;
  batchId?: string;
  batchNumber?: string;
  lotId?: string;
  lotCode?: string | null;
  receiptId?: string;
  poNumber?: string;
}

/** The simulator's answer for one order line. */
export interface FeasibilityLineResult {
  orderLineId: string;
  orderId: string;
  orderNumber: string;
  position: number;
  productId: string | null;
  description: string | null;
  qty: string;
  promisedAt: string | null;
  /** The trading day it is handed over (its promise, or today if overdue); `null` without a promise. */
  day: PlainDate | null;
  status: LineCoverageStatus;
  evaluated: boolean;
  coveredQty: string;
  missingQty: string;
  sources: LineSource[];
  /** Numbers of the batches it depends on (`covered_by_batch #n`, or the batch that fell short). */
  batchNumbers: string[];
  /** Covered, but only thanks to an expected delivery or a batch that is itself at risk. */
  atRisk: boolean;
  reason: UncoveredReason | LineNotEvaluatedReason | null;
  /** Why it is at risk or short, traced to the flag at the root (never a vague "at risk"). */
  causes: FeasibilityCause[];
  /**
   * Batches allocated to it that can't cover it (D-074): another product, for a custom item a
   * batch not planned for this line, or one not made by the promise or past its best-before
   * by the promised day. Their output goes to the shelf instead.
   */
  ignoredBatches: IgnoredBatch[];
  message: string;
}

/** A batch allocated to a line that can’t cover it, and why (D-074). */
export interface IgnoredBatch {
  batchId: string;
  number: string;
  productId: string;
  why: CoverMismatch;
  /** The trading day it is made. */
  day: PlainDate;
  /** Its output’s best-before (`null`: none kept). */
  bestBefore: PlainDate | null;
}

/** Overall status of a run. Only `all_clear` means "everything checked and fine". */
export type CoverageStatus = 'all_clear' | 'issues' | 'incomplete' | 'nothing_planned';

/** The coverage report (§5.6 step 6; lesson 12). */
export interface FeasibilityCoverage {
  status: CoverageStatus;
  /** Every batch and line in the horizon was evaluated. */
  complete: boolean;
  horizon: { from: PlainDate; to: PlainDate; days: number };
  batches: {
    total: number;
    evaluated: number;
    feasible: number;
    atRisk: number;
    infeasible: number;
    notEvaluated: { batchId: string; number: string; reasons: string[] }[];
    /** Open batches planned after the horizon (not checked yet). */
    beyondHorizon: number;
  };
  lines: {
    total: number;
    evaluated: number;
    covered: number;
    uncovered: number;
    /** Of the uncovered: lines partly covered (some of it, not all). */
    partlyCovered: number;
    atRisk: number;
    notEvaluated: {
      orderLineId: string;
      orderNumber: string;
      position: number;
      reason: LineNotEvaluatedReason;
    }[];
    beyondHorizon: number;
  };
  receipts: { total: number; simulated: number; noExpectedDate: number; beyondHorizon: number };
  /** The report in one or two sentences ("Evaluated 41 of 45 batches; 4 could not be …"). */
  summary: string;
}

/** Per product: the simulated stock from the opening balance to the end of the horizon. */
export interface FeasibilityStockSummary {
  productId: string;
  opening: string;
  received: string;
  produced: string;
  consumed: string;
  fulfilled: string;
  transferred: string;
  closing: string;
}

/** Everything the simulator answers. */
export interface FeasibilityResult {
  engineVersion: number;
  /** Today's trading day (the run's "now"). */
  businessDay: PlainDate;
  batches: FeasibilityBatchResult[];
  lines: FeasibilityLineResult[];
  coverage: FeasibilityCoverage;
  stock: FeasibilityStockSummary[];
}
